import loadable from '@loadable/component';
import type { ComponentType } from 'react';

import { useIsMobileScreen } from '@/hooks/useMedia';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import type { Holding, Portfolio } from '@/hooks/portfolio/usePortfolio';

import { useSortedUnmatchedHoldings } from '../../../../../hooks/useHoldingsSorting';
import { usePortfolioType } from '../../../hooks';

import type { RowProps, RowExpandedProps } from '../../HoldingsTableRow';

const UnmatchedHoldingRowDesktop = loadable(
  () => import('../../UnmatchedHoldingRow')
);
const UnmatchedHoldingRowMobile = loadable(
  () => import('../../UnmatchedHoldingRow'),
  { resolveComponent: (c) => c.UnmatchedHoldingRowMobile }
);

type Props = {
  portfolio: Portfolio;
  holdingId: Holding['id'] | null;
  onHoldingSelect: (id: Holding['id'] | null) => void;
  holdings: Holding[];
  row: ComponentType<RowProps>;
  rowExpanded: ComponentType<RowExpandedProps>;
};

export const Rows = ({
  portfolio,
  onHoldingSelect,
  holdingId,
  holdings,
  row: Row,
  rowExpanded: RowExpanded,
}: Props) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const unmatchedHoldings = useSortedUnmatchedHoldings(
    portfolio?.link.unmatchedHoldings
  );

  const portfolioType = usePortfolioType(portfolio);

  return (
    <>
      {[
        ...holdings.map((holding, index) =>
          !isMobileScreen && holdingId ? (
            <Row
              index={index}
              key={holding.id}
              portfolioId={portfolio.id}
              selected={holdingId === holding.id}
              portfolioType={portfolioType}
              holding={holding}
              onHoldingSelect={onHoldingSelect}
              data-cy-id={`holdings-table-row-${holding.id}`}
              data-testid={`holdings-table-row-${holding.id}`}
            />
          ) : (
            <RowExpanded
              key={holding.id}
              portfolioId={portfolio.id}
              portfolioType={portfolioType}
              holding={holding}
              onHoldingSelect={onHoldingSelect}
              data-cy-id={`holdings-table-row-${holding.id}`}
              data-testid={`holdings-table-row-${holding.id}`}
            />
          )
        ),
        ...unmatchedHoldings.map((unmatchedHolding) =>
          isMobileScreen ? (
            <UnmatchedHoldingRowMobile
              key={unmatchedHolding.id}
              unmatchedHolding={unmatchedHolding}
              data-cy-id={`holdings-table-unmatchedHolding-row-${unmatchedHolding.id}`}
              data-testid={`holdings-table-unmatchedHolding-row-${unmatchedHolding.id}`}
            />
          ) : (
            <UnmatchedHoldingRowDesktop
              key={unmatchedHolding.id}
              unmatchedHolding={unmatchedHolding}
              data-cy-id={`holdings-table-unmatchedHolding-row-${unmatchedHolding.id}`}
              data-testid={`holdings-table-unmatchedHolding-row-${unmatchedHolding.id}`}
            />
          )
        ),
      ]}
    </>
  );
};
